import React from 'react';
import RelysoftLink, { RELYSOFT_URL } from './RelysoftLink';
import { GROUP, SUPPORT } from '../CompanyInfo';
import './AppFooter.css';

/**
 * The shared app footer - one slim line.
 *
 * Was three stacked rows (a powered-by line, the contact chips and a separate
 * legal strip). The copyright is now folded into the same line as the
 * powered-by text, so the bar is a single row about a third of the height.
 *
 * Replaces the two one-line footers that read
 * `By Reliable Software, Goa. Ph: 7620789789` - one of them `position: fixed`
 * over the page content, which is what the stray `margin-bottom: 50px` on the
 * submit button was working around. `sticky` keeps it on the bottom edge of
 * the viewport while still taking up layout space, so it cannot cover
 * anything.
 *
 * @param {boolean} sticky  stick to the bottom of the viewport while scrolling
 */
function AppFooter({ sticky = false }) {
    const waHref = 'https://wa.me/' + SUPPORT.countryCode + SUPPORT.whatsapp;
    const telHref = '+' + SUPPORT.countryCode + SUPPORT.landline;

    return (
        <footer className={'appFooter' + (sticky ? ' appFooterSticky' : '')}>
            <div className="appFooterInner">
                <div className="appFooterBrand">
                    <RelysoftLink className="appFooterLogo" />
                    <span className="appFooterPowered">
                        &copy; {new Date().getFullYear()} {GROUP.name}
                        <span className="appFooterDot"> &middot; </span>
                        <span className="appFooterBy">Powered by </span>
                        <a
                            href={RELYSOFT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {SUPPORT.name}
                        </a>
                    </span>
                </div>

                <div className="appFooterContacts">
                    <a
                        className="appFooterChip appFooterApp"
                        href={GROUP.playStore}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Get it on Google Play"
                    >
                        <svg
                            className="appFooterAppIcon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <path
                                d="M4 3.2v17.6c0 .6.6 1 1.1.7l13.3-8.8c.5-.3.5-1 0-1.4L5.1 2.5C4.6 2.2 4 2.6 4 3.2z"
                                fill="currentColor"
                            />
                        </svg>
                        <span className="appFooterChipLabel">Get it on</span>
                        <span className="appFooterChipValue">Google Play</span>
                    </a>

                    {/* These are the software vendor's support lines, not
                        the distributor's - the group's own numbers are in the
                        site footer. The heading says so. */}
                    <span className="appFooterSupport">
                        <span className="appFooterSupportLabel">
                            <span className="appFooterSupportLabelFull">
                                {SUPPORT.name}
                            </span>
                            <span className="appFooterSupportLabelWord">
                                support
                            </span>
                        </span>

                        <a
                            className="appFooterChip appFooterWa"
                            href={waHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={SUPPORT.name + ' support on WhatsApp'}
                        >
                            <span className="appFooterChipLabel">WhatsApp</span>
                            <span className="appFooterChipValue">
                                {SUPPORT.whatsappDisplay}
                            </span>
                        </a>

                        <a
                            className="appFooterChip"
                            href={'tel:' + telHref}
                            title={SUPPORT.name + ' service line'}
                        >
                            <span className="appFooterChipLabel">Service</span>
                            <span className="appFooterChipValue">
                                {SUPPORT.landlineDisplay}
                            </span>
                        </a>
                    </span>
                </div>
            </div>
        </footer>
    );
}

export default AppFooter;
